<template>
  <UHeader toggle-side="left" :toggle="!hideToggle" :ui="{ root: 'bg-primary-1 border-surface-600' }">
    <template v-if="!hideToggle" #toggle>
      <UButton
        data-testid="admin-ui-navbar-sidebar-toggle"
        icon="mdi:menu"
        color="neutral"
        variant="ghost"
        class="-ms-1.5 lg:hidden"
        :ui="{ leadingIcon: 'text-surface-light' }"
        @click="toggleSidebar"
      />
    </template>

    <template #left>
      <NuxtLink to="/admin">
        <div v-if="(!isMobile && !isTablet) || !userInfo" class="AdminUINavbar__left-side__full-logo">
          <!-- Logo completo (tablets y desktop) -->
          <img
            src="/images/utamed-logo-blanco-horizontal.svg"
            alt="UTAMED"
            class="h-9 w-auto"
            data-testid="admin-ui-navbar-full-logo"
          />
        </div>
        <div v-else class="AdminUINavbar__left-side__simple-logo">
          <!-- Logo vertical (mobile) -->
          <img
            src="/images/utamed-logo-blanco-vertical.svg"
            alt="UTAMED"
            class="h-10 w-auto"
            data-testid="admin-ui-navbar-simple-logo"
          />
        </div>
      </NuxtLink>
      <CommonUIEnvTag />
    </template>

    <template #right>
      <div v-if="userInfo" class="AdminUINavbar__user">
        <UDropdownMenu data-testid="admin-ui-navbar-user-menu" :items="userMenuItems" :content="{ align: 'end' }">
          <UUser
            data-testid="admin-ui-navbar-user-avatar"
            :name="userName"
            :description="userDescription"
            :avatar="{ text: userName?.at(0), 'aria-haspopup': 'true', 'aria-controls': 'overlay_menu' }"
            :ui="{ name: 'text-surface-light', description: 'text-surface-light' }"
            class="AdminUINavbar__user-avatar"
          />
        </UDropdownMenu>
      </div>
    </template>
  </UHeader>
</template>

<script setup lang="ts">
  import type { DropdownMenuItem } from '@nuxt/ui';

  // props
  withDefaults(defineProps<{ hideToggle?: boolean }>(), {
    hideToggle: false,
  });

  // composables
  const { logout, getUserInfo } = useAuthModule();
  const { isMobile, isTablet } = useDeviceScreen();
  const toast = useToast();
  const colorMode = useColorMode();

  // stores
  const isSidebarCollapsed = useState('isSidebarCollapsed', () => false);

  // computed
  const userInfo = getUserInfo();
  const userName = userInfo?.name as string;
  const userDescription = userInfo?.email as string;

  const isDarkMode = computed({
    get: () => colorMode.value === 'dark',
    set: (value: boolean) => {
      colorMode.preference = value ? 'dark' : 'light';
    },
  });

  // functions
  function toggleSidebar() {
    isSidebarCollapsed.value = !isSidebarCollapsed.value;
  }

  async function handleLogout() {
    try {
      await logout();
    } catch {
      toast.add({
        color: 'error',
        title: 'Error al cerrar sesión',
        description: 'No se pudo cerrar la sesión. Inténtalo de nuevo.',
        duration: 5000,
      });
    }
  }

  // constants
  const userMenuItems = computed<DropdownMenuItem[][]>(() => [
    [
      {
        label: isDarkMode.value ? 'Modo claro' : 'Modo oscuro',
        icon: isDarkMode.value ? 'mdi:weather-sunny' : 'mdi:weather-night',
        onSelect: () => {
          isDarkMode.value = !isDarkMode.value;
        },
      },
    ],
    [
      {
        label: 'Cerrar sesión',
        icon: 'mdi:logout',
        color: 'error' as const,
        onSelect: handleLogout,
      },
    ],
  ]);
</script>

<style scoped>
  @reference '~/assets/css/main.css';

  .AdminUINavbar__content {
    @apply flex h-full items-center justify-between gap-5 p-2.5 lg:px-5;
  }

  .AdminUINavbar__user {
    @apply flex items-center justify-end gap-5;
  }

  .AdminUINavbar__user-avatar {
    @apply cursor-pointer;
  }
</style>
